import React, { useState } from "react";
import { Box, SvgIcon, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { ARAGON_COLORS, ARAGON_FONTS } from "../theme/AragonTheme";

function DashboardIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M4 4h6v6H4V4Zm10 0h6v6h-6V4ZM4 14h6v6H4v-6Zm10 0h6v6h-6v-6Z" fill="none" stroke="currentColor" strokeWidth="1.6" />
    </SvgIcon>
  );
}
function ValuationIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M5 19V9m5 10V5m5 14v-7m4 7V3M3 19h18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </SvgIcon>
  );
}
function DealRoomIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M5 8h14v11H5V8Zm4-3h6l1 3H8l1-3Zm-4 7h14" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinejoin="round" />
    </SvgIcon>
  );
}
function DocumentIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M7 3h7l4 4v14H7V3Zm7 0v5h5M10 12h5M10 15h5M10 18h4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </SvgIcon>
  );
}
function CalendarIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M5 6h14v13H5V6Zm3-3v5m8-5v5M5 10h14" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" strokeLinejoin="round" />
    </SvgIcon>
  );
}
function WatchlistIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M7 4h10v16l-5-3.2L7 20V4Z" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinejoin="round" />
    </SvgIcon>
  );
}
function NdaIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M7 3h7l4 4v14H7V3Zm7 0v5h5M9.5 13h5m-5 3h3.5m2.5 1.7 2.8-2.8 1.1 1.1-2.8 2.8-1.6.5.5-1.6Z" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" />
    </SvgIcon>
  );
}

const SELLER_NAV_ITEMS = [
  { id: "dashboard", label: "Dashboard", path: "/seller/dashboard", Icon: DashboardIcon },
  { id: "valuation", label: "Valuation", path: "/seller/valuation", Icon: ValuationIcon },
  { id: "deal-room", label: "Deal Room", path: "/seller/deal-room", Icon: DealRoomIcon },
  { id: "documents", label: "Documents", path: "/seller/documents", Icon: DocumentIcon },
];
const BUYER_NAV_ITEMS = [
  { id: "dashboard", label: "Dashboard", path: "/buyer/dashboard", Icon: DashboardIcon },
  { id: "watchlist", label: "Watchlist", path: "/buyer/watchlist", Icon: WatchlistIcon },
  { id: "nda", label: "NDA", path: "/buyer/nda", Icon: NdaIcon },
];
const ADVISOR_NAV_ITEMS = [
  { id: "dashboard", label: "Dashboard", path: "/advisor/dashboard", Icon: DashboardIcon },
  { id: "calendar", label: "Calendar", path: "/advisor/calendar", Icon: CalendarIcon },
  { id: "deal-room", label: "Deal Room", path: "/advisor/deal-room", Icon: DealRoomIcon },
  { id: "documents", label: "Documents", path: "/advisor/documents", Icon: DocumentIcon },
];
const WORKSPACE_NAV_ITEMS = { seller: SELLER_NAV_ITEMS, buyer: BUYER_NAV_ITEMS, advisor: ADVISOR_NAV_ITEMS };

export default function WorkspaceSidebar({ metrics, onExpandedChange, workspace = "seller" }) {
  const [expanded, setExpanded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const logoInset = Math.round((metrics.sidebarCollapsed - metrics.logoSize) / 2);
  const navItems = WORKSPACE_NAV_ITEMS[workspace] || SELLER_NAV_ITEMS;
  const goTo = (path) => navigate(path, workspace === "buyer" ? { state: location.state } : undefined);
  return (
    <Box
      component="aside"
      onMouseEnter={() => {
        setExpanded(true);
        onExpandedChange?.(true);
      }}
      onMouseLeave={() => {
        setExpanded(false);
        onExpandedChange?.(false);
      }}
      onFocus={() => {
        setExpanded(true);
        onExpandedChange?.(true);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setExpanded(false);
          onExpandedChange?.(false);
        }
      }}
      sx={{
        position: "fixed",
        inset: "0 auto 0 0",
        width: `${expanded ? metrics.sidebarExpanded : metrics.sidebarCollapsed}px`,
        height: "100dvh",
        zIndex: 1400,
        overflow: "visible",
        bgcolor: ARAGON_COLORS.sellerSidebar,
        transition: "width 220ms cubic-bezier(0.22, 1, 0.36, 1)",
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          width: "100%",
          height: `${metrics.sidebarCollapsed}px`,
          display: "flex",
          alignItems: "center",
          pl: `${logoInset}px`,
          bgcolor: ARAGON_COLORS.sellerSidebar,
          boxSizing: "border-box",
        }}
      >
        <Box component="img" src="/images/1080 x 1080 px Logo 1.png" alt="Aragon" sx={{ width: `${metrics.logoSize}px`, height: `${metrics.logoSize}px`, objectFit: "cover", borderRadius: "5px", flex: "0 0 auto" }} />
        <Typography sx={{ ml: "10px", opacity: expanded ? 1 : 0, transform: expanded ? "translateX(0)" : "translateX(-5px)", transition: "opacity 140ms ease 55ms, transform 180ms ease 35ms", fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.sidebarLabelSize}px`, fontWeight: 700, color: ARAGON_COLORS.navy, whiteSpace: "nowrap" }}>
          Aragon
        </Typography>
      </Box>
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          top: `${metrics.mainCornerRadius}px`,
          right: 0,
          bottom: 0,
          width: "1px",
          bgcolor: alpha(ARAGON_COLORS.navy, 0.10),
          pointerEvents: "none",
        }}
      />
      <Box sx={{ mt: `${metrics.sidebarNavTop}px`, display: "flex", flexDirection: "column", gap: `${metrics.sidebarItemGap}px`, width: "100%" }}>
        {navItems.map(({ id, label, path, Icon }) => {
          const active = location.pathname === path;
          return (
            <Box
              key={id}
              role="button"
              tabIndex={0}
              onClick={() => goTo(path)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") goTo(path);
              }}
              sx={{
                height: `${metrics.sidebarItemHeight}px`,
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                bgcolor: active ? ARAGON_COLORS.sellerCanvas : "transparent",
                color: active ? ARAGON_COLORS.navy : alpha(ARAGON_COLORS.navy, 0.72),
                transition: "background-color 140ms ease, color 140ms ease",
                "&:hover": { bgcolor: active ? ARAGON_COLORS.sellerCanvas : alpha(ARAGON_COLORS.navy, 0.045), color: ARAGON_COLORS.navy },
                "&:focus-visible": { outline: `1px solid ${alpha(ARAGON_COLORS.navy, 0.30)}`, outlineOffset: "-2px" },
              }}
            >
              <Box sx={{ width: `${metrics.sidebarCollapsed}px`, height: "100%", display: "grid", placeItems: "center", flex: "0 0 auto" }}>
                <Icon sx={{ fontSize: `${metrics.sidebarIconSize}px` }} />
              </Box>
              <Typography sx={{ ml: "8px", opacity: expanded ? 1 : 0, transform: expanded ? "translateX(0)" : "translateX(-5px)", transition: "opacity 140ms ease 55ms, transform 180ms ease 35ms", whiteSpace: "nowrap", fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.sidebarLabelSize}px`, fontWeight: active ? 700 : 500, letterSpacing: "0.005em" }}>
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
